import { BASE_PATHS } from '@belong/common';
import type { PropertySummary } from 'api/models';
import { OrderedAgreementStepEnums } from 'models/enums';

export function getSaveAndExitPath({
  properties,
}: Record<'properties', PropertySummary[]>) {
  if (properties?.length > 1) {
    return BASE_PATHS.HOMEOWNER_SETUP_FLOW;
  }

  if (properties?.length === 1) {
    // Both assertions are guarded by the length check above and by the read
    // already being unconditional.
    return `${BASE_PATHS.HOMEOWNER_SETUP_FLOW}/${properties[0]!.basicInfo!.propertyId}`;
  }

  return BASE_PATHS.ACCOUNTS;
}

export function getApiErrorMessage(error: unknown): string | undefined {
  if (!Array.isArray(error)) {
    return undefined;
  }

  const message: unknown = error[0]?.message;

  return typeof message === 'string' && message.trim() ? message : undefined;
}

/** A 400 on loading a step: the platform refused it with copy meant for the homeowner, e.g. a voided update. */
export class AgreementStepRefusedError extends Error {
  constructor(readonly reason: string) {
    super(reason);
    this.name = 'AgreementStepRefusedError';
  }
}

export function toAgreementStepError(error: unknown): unknown {
  const response = (error as { response?: { status?: number; data?: unknown } })
    ?.response;
  const reason =
    response?.status === 400 ? getApiErrorMessage(response.data) : undefined;

  return reason ? new AgreementStepRefusedError(reason) : error;
}

type FlowStep = { stepName?: string; isHidden?: boolean; status?: string };

export function findFirstOpenStep<T extends FlowStep>(
  steps: T[] | null | undefined,
): T | undefined {
  return OrderedAgreementStepEnums.map((stepName) =>
    steps?.find((step) => step.stepName === stepName),
  ).find(
    (step) =>
      step?.isHidden === false &&
      ['Created', 'Incomplete'].includes(step.status ?? ''),
  );
}
